import { SetupWindow } from "./../Pages/Shell/SetupWindow";
import { TerminalWindow } from "./../Pages/Shell/TerminalWindow";
import { PasswordEditWindow } from "./PasswordEditWindow";
import { AdminWatchWindow } from "./AdminWatchWindow";
import { PasswordNormalizer, PasswordSanitizer } from "../Utils/PasswordSanitizer";
import { DeviceWatchersWindow } from "./DeviceWatchersWindow";
import { DeviceRecordWindow } from "./DeviceRecordWindow";
import { DeviceProfileWindow } from "./DeviceProfileWindow";
import { Device } from "../Services/Device";
import { Menu } from "./Menu";
import { CopyToClipboard } from "../Utils/CopyToClipboard";


export class DeviceMenuWindow extends Menu
{
    constructor(device: Device)
    {
        super(device.Id + " " + (device.IsLocked ? "🔒" : "🔓" + device.Password.value), {
            "📺 Watch": () => this.Watch(device),
            "🐓 Admin": () => new AdminWatchWindow(device),
            "🚀 Launch": () => this.Launch(device),
            "🔑 Lock": () => this.Lock(device),
            "🎲 Regenerate": () => this.Regenerate(device),
            "📝 Edit": () => new PasswordEditWindow(device),
            "📱 SMS": () => this.SMS(device),
            "🧩 Access": () => this.Access(device),
            "🐌 Shell": () => new TerminalWindow(device),
            "📡 Setup": () => new SetupWindow(device),
            "💾 Storage": () => this.OpenStorage(device),
            "👀 Watchers": () => new DeviceWatchersWindow(device),
            "🖼️ Profile": () => new DeviceProfileWindow(device),
            "💽 Record": () => new DeviceRecordWindow(device),
            "🧨 Kill": () => this.Kill(device),
            "⚡ Reboot": () => this.Reboot(device),
        });

        this.MaxWidth(600);
    }

    private WatcherLoginUrl(device: Device): string
    {
        const watcherLoginPage = process.env.WATCHER_LOGIN_PAGE || "";
        const serverId = device.ServerId;

        return watcherLoginPage.replace("{server}", serverId);
    }

    private LaunchWatcherUrl(device: Device): string
    {
        const watcherWatchPage = process.env.WATCHER_WATCH_PAGE || "";
        const serverId = device.ServerId;
        const passowrd = PasswordSanitizer.Sanitize(device.Password.value);

        return watcherWatchPage.replace("{server}", serverId).replace("{password}", passowrd);
    }

    private async Access(device: Device): Promise<void>
    {
        if (device.IsLocked)
            await this.Regenerate(device);

        this.Do(
            "🧩 Generating access...",
            () =>
            {
                CopyToClipboard(JSON.stringify({
                    "Access": "TX-Watcher",
                    "Version": "4.0.0",
                    "DeviceId": device.Id,
                    "Login": device.Id,
                    "Password": PasswordNormalizer.Normalize(device.Password.value),
                    "LoginLink": this.WatcherLoginUrl(device),
                    "LaunchLink": this.LaunchWatcherUrl(device)
                }))
            },
            "🧩 Access data copied to clipboard ✔");
    }

    private Watch(device: Device): void
    {
        window.open(this.LaunchWatcherUrl(device));
    }

    private async Launch(device: Device): Promise<void>
    {
        if (device.IsPrivate)
        {
            if (!confirm("Device is private. Are you sure you want to regenerate the password?"))
            {
                return;
            }
        }

        if (!device.IsLocked)
        {
            if (!confirm("Password already set! Are you sure you want to regenerate the password?"))
            {
                return;
            }
        }

        await this.DoAsync(
            "🚀 Regenerating password...",
            async () => await device.RegeneratePasswordAndCopySMSMessage(),
            "🚀 SMS copied to clipboard ✔");
    }

    private async Lock(device: Device): Promise<void>
    {
        if (device.IsPrivate)
        {
            if (!confirm("Device is private. Are you sure you want to lock it?"))
            {
                return;
            }
        }

        await this.DoAsync(
            "🔑 Locking...",
            async () => await device.ResetPassword(),
            "🔑 Locked ✔");
    }

    private async Regenerate(device: Device): Promise<void>
    {
        if (device.IsPrivate)
        {
            if (!confirm("Device is private. Are you sure you want to regenerate the password?"))
            {
                return;
            }
        }

        if (!device.IsLocked)
        {
            if (!confirm("Password already set! Are you sure you want to regenerate the password?"))
            {
                return;
            }
        }

        await this.DoAsync(
            "🎲 Regenerating password...",
            async () => await device.RegeneratePassword(),
            "🎲 New password set ✔");
    }

    private async SMS(device: Device): Promise<void>
    {
        await this.DoAsync(
            "📧 Generating SMS...",
            async () => await device.GenerateSMSAndCopyToClipboard(),
            "📧 Copied to clipboard ✔");
    }

    private OpenStorage(device: Device): void
    {
        window.open(process.env.DROPBOX_PAGE + '/' + device.Password.value, '_blank')
    }

    private Kill(device: Device): void
    {
        this.MakeSure(`Kill ${device.Id}?`, () =>
        {
            this.Do(
                "🧨 Killing...",
                async () => await device.Kill(),
                "🧨 Killed ✔");
        });
    }

    private Reboot(device: Device): void
    {
        this.MakeSure(`Reboot ${device.Id}?`, () =>
        {
            this.Do(
                "⚡ Rebooting...",
                async () => await device.Reboot(),
                "⚡ Rebooted ✔");
        });
    }

    private async DoAsync(before: string, callback: () => Promise<any>, after: string): Promise<void>
    {
        try
        {
            this.Result.Text(before).Color("#222");
            await callback();
            this.Result.Text(after);
        }
        catch (ex)
        {
            this.Result.Text(ex.message).Color("red");
        }
    }

    private Do(before: string, callback: () => void, after: string): void
    {
        try
        {
            this.Result.Text(before).Color("#222");
            callback();
            this.Result.Text(after);
        }
        catch (ex)
        {
            this.Result.Text(ex.message).Color("red");
        }
    }

    private MakeSure(label: string, callback: () => void)
    {
        if (confirm(label))
            callback();
    }
}
